import React, { createContext, useContext, useEffect, useRef } from 'react'
import {
    namedOperations,
    useInternalChatQuery,
    InternalChatFieldsFragment,
    useInternalChatMessagesQuery,
    InternalChatMessageFieldsFragment,
    InternalChatUserPreviewFieldsFragment,
    useUpdateInternalChatUserLastReadMutation,
} from '~graphql/generated/graphql'
import { useUserContext } from '~config/user-context'
import { configureAbly, useChannel } from '@ably-labs/react-hooks'
import { ChannelTypeNames, constructChannel } from '~utils/ably-helpers'

export function useTeamChatConversation() {
    return useContext(TeamChatConversationContext)
}

interface Props {
    children: React.ReactNode
    chatId: string
}

export default function TeamChatConversationProvider({
    children,
    chatId,
}: Props) {
    const { user: authUser } = useUserContext()
    const page = useRef<number>(0)
    const hasReachedEnd = useRef<boolean>(false)

    const { data, loading } = useInternalChatQuery({
        variables: {
            id: chatId,
        },
        skip: !chatId,
    })

    const {
        data: messagesData,
        previousData,
        networkStatus,
        refetch,
        fetchMore,
    } = useInternalChatMessagesQuery({
        variables: {
            chatId,
            page: 0,
            pageSize: 25,
        },
        skip: !chatId,
        notifyOnNetworkStatusChange: true,
    })

    const [updateLastReadTime] = useUpdateInternalChatUserLastReadMutation({
        refetchQueries: [
            namedOperations.Query.queryInternalChatUsers,
            namedOperations.Query.countInternalChatUsers,
        ],
    })

    useEffect(() => {
        refetch()
        updateLastReadTime({
            variables: {
                chatId,
                userId: authUser?.id || '',
                lastReadTime: Date.now(),
            },
        })
    }, [chatId])

    const internalChat = data?.internalChat as InternalChatFieldsFragment
    const currentInternalChatUser = (
        internalChat?.internal_chat_users || []
    ).find(user => user.user.id === authUser?.id)
    const internalChatUsers = (internalChat?.internal_chat_users || []).filter(
        user => user.user.id !== authUser?.id,
    )
    const messages =
        messagesData?.internalChatMessages ||
        previousData?.internalChatMessages ||
        []
    const hasMoreMessages = messages.length % 25 === 0 && messages.length !== 0

    async function handleFetchMore() {
        try {
            await fetchMore({
                variables: {
                    chatId,
                    page: page.current + 1,
                    pageSize: 25,
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev
                    return {
                        internalChatMessages: [
                            ...(prev.internalChatMessages || []),
                            ...(fetchMoreResult.internalChatMessages || []),
                        ],
                    }
                },
            })
            page.current = page.current + 1
        } catch (error) {
            console.error('error', error)
            hasReachedEnd.current = true
        }
    }

    configureAbly({
        authUrl: `${process.env.GATSBY_REST_API_URL}/ably/auth`,
    })

    useChannel(
        constructChannel(ChannelTypeNames.newInternalChatMessage, chatId),
        () => {
            refetch()
            updateLastReadTime({
                variables: {
                    chatId,
                    userId: authUser?.id || '',
                    lastReadTime: Date.now(),
                },
            })
        },
    )

    return (
        <TeamChatConversationContext.Provider
            value={{
                internalChat,
                internalChatUsers,
                currentInternalChatUser,
                loading,

                messages,
                hasMoreMessages,
                fetchMore: handleFetchMore,
                networkStatus,
            }}
        >
            {children}
        </TeamChatConversationContext.Provider>
    )
}

type TeamChatConversationContextType = {
    internalChat: InternalChatFieldsFragment
    internalChatUsers: InternalChatUserPreviewFieldsFragment[]
    currentInternalChatUser: InternalChatUserPreviewFieldsFragment | undefined
    loading: boolean

    messages: InternalChatMessageFieldsFragment[]
    hasMoreMessages: boolean
    fetchMore: () => Promise<void>
    networkStatus: number
}

const TeamChatConversationContext =
    createContext<TeamChatConversationContextType>(
        {} as TeamChatConversationContextType,
    )
